import React, { useState } from 'react';
import { ClipboardList, ChevronDown, ChevronUp, ShieldCheck, Banknote, CheckCircle2, Circle, Clock } from 'lucide-react';
import useGameStore from '../../store/gameStore';
import { formatMoney } from '../../utils/format';
import { fleetOpSteps, isFleetOpOpen } from '../../store/helpers/fleetOpsLog';

const KIND_META = {
  sale: { label: 'Vente', Icon: Banknote, tone: 'text-money border-money/40 bg-money/10' },
  claim: { label: 'Sinistre', Icon: ShieldCheck, tone: 'text-sky-300 border-sky-500/40 bg-sky-500/10' },
};

const CLAIM_TYPE_LABELS = { repair: 'Réparation', theft: 'Vol de véhicule', breakdown: 'Panne' };

function OpRow({ op, totalDays }) {
  const meta = KIND_META[op.kind] || KIND_META.claim;
  const steps = fleetOpSteps(op);
  const open = isFleetOpOpen(op);
  const amountLabel = op.kind === 'sale'
    ? `${formatMoney(op.amount)} ${op.status === 'credited' ? `crédités J${op.creditedDay}` : `attendus J${op.payoutDay}`}`
    : op.status === 'refused'
      ? 'Indemnisation refusée'
      : `${formatMoney(op.amount)} ${op.status === 'paid' ? (op.directSettlement ? 'pris en charge' : `versés J${op.creditedDay}`) : `prévus J${op.payoutDay}`}`;
  return (
    <li className={`px-3 py-2.5 ${open ? '' : 'opacity-75'}`} data-testid={`fleet-op-${op.id}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border ${meta.tone}`}>
            {meta.label}{op.kind === 'claim' && op.claimType ? ` · ${CLAIM_TYPE_LABELS[op.claimType] || op.claimType}` : ''}
          </span>
          <span className="text-xs font-semibold text-slate-200 truncate">{op.vehicleLabel}</span>
          <span className="text-[10px] text-slate-500 font-mono">n°{String(op.id).slice(-6).toUpperCase()} · ouvert J{op.createdDay}</span>
        </div>
        <span className={`text-xs font-bold ${op.status === 'refused' ? 'text-danger' : open ? 'text-amber-300' : 'text-money'}`} data-testid={`fleet-op-amount-${op.id}`}>
          {amountLabel}
        </span>
      </div>
      <ol className="flex items-center gap-1.5 mt-2 flex-wrap" data-testid={`fleet-op-steps-${op.id}`}>
        {steps.map((s, i) => (
          <li key={s.key} className="flex items-center gap-1.5">
            {i > 0 && <span className={`h-px w-4 ${s.done ? 'bg-money/60' : 'bg-ink-600'}`} />}
            <span className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded ${s.done ? (s.current && open ? 'bg-amber-500/15 text-amber-300' : 'bg-money/10 text-money') : 'bg-ink-700/40 text-slate-500'}`}>
              {s.done ? (s.current && open ? <Clock className="w-3 h-3" /> : <CheckCircle2 className="w-3 h-3" />) : <Circle className="w-3 h-3" />}
              {s.label}{Number.isFinite(s.day) ? <span className="font-mono opacity-70">J{s.day}</span> : null}
            </span>
          </li>
        ))}
      </ol>
      {op.kind === 'claim' && op.status !== 'refused' && (
        <p className="text-[10px] text-slate-500 mt-1">
          Couverture {op.tier || 'standard'} {Math.round((op.coverageRate || 0) * 100)} % · franchise {formatMoney(op.franchise || 0)}
          {op.claimType === 'theft' ? ` · valeur vénale ${formatMoney(op.repairCost || 0)}` : ` · réparation ${formatMoney(op.repairCost || 0)}`}
          {open && op.payoutDay != null ? ` · versement dans ${Math.max(0, op.payoutDay - totalDays)} j` : ''}
        </p>
      )}
    </li>
  );
}

/** Flotte — suivi des ventes de véhicules et des dossiers d'assurance. */
export default function FleetOpsTracker() {
  const log = useGameStore((s) => s.fleetOpsLog) || [];
  const totalDays = useGameStore((s) => s.totalDays) || 0;
  const [expanded, setExpanded] = useState(true);
  const [showAll, setShowAll] = useState(false);
  if (log.length === 0) return null;
  const openCount = log.filter(isFleetOpOpen).length;
  const visible = showAll ? log : log.slice(0, 5);
  return (
    <div className="bg-ink-800 rounded-lg border border-ink-600" data-testid="fleet-ops-tracker">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between px-3 py-2.5 text-left"
        data-testid="fleet-ops-toggle"
      >
        <span className="flex items-center gap-2 text-sm font-semibold text-slate-200">
          <ClipboardList className="w-4 h-4 text-accent" /> Opérations &amp; sinistres
          <span className="text-[10px] font-normal text-slate-500">{log.length} dossier{log.length > 1 ? 's' : ''}{openCount > 0 ? ` · ${openCount} en cours` : ''}</span>
        </span>
        {expanded ? <ChevronUp className="w-4 h-4 text-slate-500" /> : <ChevronDown className="w-4 h-4 text-slate-500" />}
      </button>
      {expanded && (
        <>
          <ul className="divide-y divide-ink-700 border-t border-ink-700" data-testid="fleet-ops-list">
            {visible.map((op) => <OpRow key={op.id} op={op} totalDays={totalDays} />)}
          </ul>
          {log.length > 5 && (
            <button type="button" onClick={() => setShowAll((v) => !v)} className="w-full text-[11px] text-accent py-1.5 hover:bg-ink-700/40" data-testid="fleet-ops-show-all">
              {showAll ? 'Réduire' : `Voir les ${log.length - 5} dossiers plus anciens`}
            </button>
          )}
        </>
      )}
    </div>
  );
}
